import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { EmployeeOption, EmployeeSearchPicker } from '@/modules/shared/hr-lookups';
import { useRequestSickLeave } from '../../hooks/use-sick-leave';
import {
  doctorLeaveRequestFormSchema,
  DoctorLeaveRequestFormValues,
} from '../../schemas/doctor-leave-request-form-schema';

export interface DoctorLeaveRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DoctorLeaveRequestDialog = ({ open, onOpenChange }: DoctorLeaveRequestDialogProps) => {
  const { t } = useTranslation();
  const [employee, setEmployee] = useState<EmployeeOption | null>(null);

  const { mutateAsync: requestSickLeave, isPending } = useRequestSickLeave();

  const form = useForm<DoctorLeaveRequestFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(doctorLeaveRequestFormSchema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { UserId: '', StartDate: '', EndDate: '', Reason: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ UserId: '', StartDate: '', EndDate: '', Reason: '' });
    setEmployee(null);
  }, [open, form]);

  // A refusal (no permission, no Sick Leave category configured, ...) comes back as
  // `IsSuccessful: false` with its own message, toasted by `useRequestSickLeave`; the dialog stays open
  // then.
  const handleSubmit = async (values: DoctorLeaveRequestFormValues) => {
    try {
      const result = await requestSickLeave({
        userId: values.UserId,
        startDate: values.StartDate,
        endDate: values.EndDate,
        reason: values.Reason,
      });
      if (result.IsSuccessful) onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('FILE_DOCTOR_CERTIFIED_SICK_LEAVE')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="UserId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('EMPLOYEE')}</FormLabel>
                  <FormControl>
                    <EmployeeSearchPicker
                      value={field.value}
                      selectedLabel={employee?.DisplayName}
                      onSelect={(picked) => {
                        setEmployee(picked);
                        field.onChange(picked.UserId);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="StartDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('START_DATE')}</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="EndDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('END_DATE')}</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="Reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('REASON')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} maxLength={500} placeholder={t('REASON')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:gap-0">
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('SUBMIT')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
